import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authedFetch } from '../api'

export interface Agent {
  id: string
  name: string
  tool_command: string
  prompt: string
  accuracy: number | null
  avg_cost: number | null
  total_score: number
  failure_count: number
  pass_count: number
  fail_count: number
  dsq_count: number
  disagree_count: number
  created_at: string
}

interface AgentsState {
  items: Agent[]
  loading: boolean
  error: string | null
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

const initialState: AgentsState = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  page: 0,
  pageSize: 50,
  hasMore: true,
}

export const fetchAgents = createAsyncThunk('agents/fetchAgents', async () => {
  const response = await fetch('/api/agents?page=1&pageSize=50')
  if (!response.ok) throw new Error('Failed to fetch agents')
  return response.json() as Promise<{ items: Agent[]; total: number; page: number; pageSize: number }>
})

export const fetchMoreAgents = createAsyncThunk('agents/fetchMoreAgents', async (page: number) => {
  const response = await fetch(`/api/agents?page=${page}&pageSize=50`)
  if (!response.ok) throw new Error('Failed to fetch agents')
  return response.json() as Promise<{ items: Agent[]; total: number; page: number; pageSize: number }>
})

export const createAgent = createAsyncThunk(
  'agents/createAgent',
  async (agent: Omit<Agent, 'id' | 'accuracy' | 'avg_cost' | 'total_score' | 'failure_count' | 'pass_count' | 'fail_count' | 'dsq_count' | 'disagree_count' | 'created_at'>) => {
    const response = await authedFetch('/api/agents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(agent),
    })
    if (!response.ok) throw new Error('Failed to create agent')
    return response.json() as Promise<Agent>
  }
)

export const deleteAgent = createAsyncThunk(
  'agents/deleteAgent',
  async (id: string) => {
    const response = await authedFetch(`/api/agents/${id}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete agent')
    return id
  }
)

const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        if (state.items.length === 0) state.loading = true
        state.error = null
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
        state.hasMore = action.payload.page * action.payload.pageSize < action.payload.total
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch agents'
      })
      .addCase(fetchMoreAgents.fulfilled, (state, action) => {
        state.items = [...state.items, ...action.payload.items]
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
        state.hasMore = action.payload.page * action.payload.pageSize < action.payload.total
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.items.unshift(action.payload)
        state.total += 1
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.items = state.items.filter((a) => a.id !== action.payload)
        state.total -= 1
      })
  },
})

export default agentsSlice.reducer
